import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { Card } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch'; // used by HeroSwitch
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

// ─── Project picker dropdown ──────────────────────────────────────────────────

export const ProjectPickerPopover = styled(Paper)(({ theme }) => ({
  width: 260,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 14,
  boxShadow: '0 8px 32px -8px rgba(47,39,117,0.18), 0 2px 8px rgba(0,0,0,0.08)',
  overflow: 'hidden',
  marginTop: 6,
}));

export const ProjectPickerSearch = styled(InputBase)(({ theme }) => ({
  width: '100%',
  padding: `${theme.spacing(1.25)} ${theme.spacing(2)}`,
  fontSize: 13,
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& input::placeholder': { color: theme.palette.text.disabled },
}));

export const ClientGroupHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `${theme.spacing(0.75)} ${theme.spacing(2)}`,
  backgroundColor: theme.palette.secondary.light,
  cursor: 'pointer',
  userSelect: 'none',
  '&:hover': { backgroundColor: theme.palette.secondary.light },
}));

export const ClientGroupLabel = styled(Typography)(({ theme }) => ({
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: theme.palette.primary.main,
}));

export const ProjectPickerItem = styled(Box)<{ selected?: number }>(({ theme, selected }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)} ${theme.spacing(1)} ${theme.spacing(2.5)}`,
  cursor: 'pointer',
  borderLeft: selected ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
  backgroundColor: selected ? theme.palette.primary.light + '33' : 'transparent',
  '&:hover': { backgroundColor: theme.palette.secondary.light },
}));

export const ProjectPickerItemName = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 500,
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: theme.palette.text.primary,
}));

export const ProjectPickerList = styled(Box)(({ theme }) => ({
  maxHeight: 340,
  overflowY: 'auto',
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.primary.light} transparent`,
  '&::-webkit-scrollbar': {
    width: 4,
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.primary.light,
    borderRadius: 4,
  },
}));

// ─── Mode toggle ──────────────────────────────────────────────────────────────

export const ModeToggleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#FFFFFF',
  borderRadius: '10px',
  padding: theme.spacing(0.8),
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(2),
  width: 'fit-content',
  border: `1px solid ${theme.palette.divider}`,
}));

export const ModeButton = styled(Button)<{ active?: number }>(({ theme, active }) => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: 14,
  padding: `${theme.spacing(0.5)} ${theme.spacing(2.5)}`,
  backgroundColor: active ? theme.palette.secondary.main : 'transparent',
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  boxShadow: active ? '0 1px 3px rgba(0,0,0,0.15)' : 'none',
  minWidth: 0,
  '&:hover': {
    backgroundColor: active ? theme.palette.secondary.main : theme.palette.action.selected,
    boxShadow: active ? '0 1px 3px rgba(0,0,0,0.15)' : 'none',
  },
}));

// ─── Timer bar ────────────────────────────────────────────────────────────────

export const TimerBarPaper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: 'none',
  marginBottom: theme.spacing(3),
}));

export const DescriptionInput = styled(InputBase)(({ theme }) => ({
  flexGrow: 1,
  fontSize: 14,
  color: theme.palette.text.primary,
  '& input::placeholder': {
    color: theme.palette.text.disabled,
    padding: 4,
  },
  backgroundColor: '#F4F6FB',
  padding: 8,
  borderRadius: 8,
}));

export const BarDivider = styled(Box)(({ theme }) => ({
  width: 1,
  height: 24,
  backgroundColor: theme.palette.divider,
  flexShrink: 0,
}));

export const ProjectPickerButton = styled(Button)<{ active?: number }>(({ theme, active }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: 15,
  height: 44,
  gap: theme.spacing(0.75),
  minWidth: 0,
  padding: `${theme.spacing(0)} ${theme.spacing(2)}`,
  border: `1.5px solid ${active ? theme.palette.primary.light : 'rgba(255,255,255,0.35)'}`,
  borderRadius: 10,
  color: '#fff',
  backgroundColor: active ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.10)',
  backdropFilter: 'blur(4px)',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.22)',
    borderColor: 'rgba(255,255,255,0.55)',
  },
}));

export const BillableIcon = styled(AttachMoneyIcon)<{ active?: number }>(({ theme, active }) => ({
  fontSize: 20,
  cursor: 'pointer',
  color: active ? theme.palette.secondary.main : theme.palette.text.disabled,
  transition: 'color 0.15s',
}));

export const TimerDisplay = styled(Typography)(({ theme }) => ({
  fontVariantNumeric: 'tabular-nums',
  fontSize: 20,
  fontWeight: 600,
  color: theme.palette.text.primary,
  minWidth: 90,
  textAlign: 'center',
  letterSpacing: 1,
}));

export const TimerDisplayInput = styled(InputBase)(({ theme }) => ({
  fontVariantNumeric: 'tabular-nums',
  fontSize: 20,
  fontWeight: 600,
  color: theme.palette.text.primary,
  minWidth: 90,
  letterSpacing: 1,
  '& input': {
    textAlign: 'center',
    padding: 0,
    width: 54,
  },
}));

export const StartButton = styled(Button)(({ theme }) => ({
  minWidth: 90,
  fontWeight: 700,
  fontSize: 15,
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none',
}));

export const StartTimeText = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
  textTransform: 'uppercase',
  letterSpacing: 0.6,
}));

// Manual mode extras

export const DatePickerButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.5),
}));

export const TimeInput = styled(InputBase)(({ theme }) => ({
  fontSize: 14,
  width: 54,
  textAlign: 'center',
  '& input': { textAlign: 'center' },
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const TimeSeparator = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.disabled,
  fontSize: 14,
  lineHeight: '24px',
  paddingLeft: theme.spacing(0.5),
  paddingRight: theme.spacing(0.5),
}));

export const AddEntryButton = styled(Button)(({ theme }) => ({
  minWidth: 70,
  fontWeight: 700,
  fontSize: 14,
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none',
}));

// ─── Week navigation (manual mode) ────────────────────────────────────────────

export const WeekNavRow = styled(Box)<{ automargin?: number }>(({ theme, automargin }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
  marginBottom: automargin ? 0 : theme.spacing(2),
  marginLeft: automargin ? 'auto' : undefined,
}));

export const WeekLabel = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.secondary,
  minWidth: 80,
  textAlign: 'center',
}));

// ─── Page layout ──────────────────────────────────────────────────────────────

export const TrackerLayout = styled(Box)({
  display: 'flex',
  gap: 24,
  alignItems: 'flex-start',
});

export const EntriesScrollBox = styled(Box)({
  maxHeight: 'calc(100vh - 250px)',
  overflowY: 'auto',
  paddingRight: 4,
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
});

export const EntriesColumn = styled(Box)({
  flex: 1,
  minWidth: 0,
});

export const SummaryColumn = styled(Box)({
  width: 280,
  flexShrink: 0,
});

// ─── Week group header ────────────────────────────────────────────────────────

export const WeekGroupHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `${theme.spacing(0.5)} ${theme.spacing(0.5)}`,
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
}));

export const WeekDateRange = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.text.secondary,
}));

export const WeekTotalTime = styled(Typography)(({ theme }) => ({
  fontVariantNumeric: 'tabular-nums',
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.text.secondary,
}));

export const WeekGroupCard = styled(Card)(({ theme }) => ({
  borderRadius: '24px',
  boxShadow: '0px 2px 12px #6366F114',
  marginBottom: theme.spacing(2),
  overflow: 'hidden',
  padding: '0px 12px',
}));

// ─── Day group ────────────────────────────────────────────────────────────────

export const DayGroupCard = styled(Paper)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.secondary.light}`,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0px 2px 12px #6366F114',
  marginBottom: theme.spacing(2),
  overflow: 'hidden',
}));

export const DayGroupHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: `${theme.spacing(1.25)} ${theme.spacing(2)}`,
  backgroundColor: theme.palette.secondary.light,
  borderBottom: `1px solid ${theme.palette.secondary.light}`,
  height: 48,
}));

export const DayLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: 14,
  color: theme.palette.text.primary,
  // flex: 1,
}));

export const DayTotal = styled(Typography)(({ theme }) => ({
  fontVariantNumeric: 'tabular-nums',
  fontSize: 14,
  fontWeight: 600,
  color: theme.palette.secondary.dark,
  marginRight: theme.spacing(1),
}));

export const SessionCountBadge = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 500,
  color: theme.palette.brandMuted,
  marginRight: theme.spacing(1),
  marginLeft: theme.spacing(2),
  flex: 1,
}));

export const ProjectGroupLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: `${theme.spacing(0.75)} ${theme.spacing(2)}`,
  backgroundColor: theme.palette.background.default,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const ProjectBadge = styled(Chip)(({ theme }) => ({
  height: 22,
  fontSize: 12,
  fontWeight: 600,
  borderRadius: 4,
  color: '#fff',
  '& .MuiChip-label': { paddingLeft: 8, paddingRight: 8 },
}));

export const ClientNameLabel = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  opacity: 0.6,
  color: theme.palette.text.secondary,
}));

export const ProjectGroupSeparator = styled('div')<{ isfirst?: number }>(({ theme, isfirst }) => ({
  borderTop: isfirst ? 'none' : `1px solid ${theme.palette.divider}`,
}));

export const EntryRowsContainer = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  paddingLeft: theme.spacing(2),
}));

export const MenuItemDeleteIcon = styled(DeleteOutlineIcon)({
  opacity: 0.6,
  fontSize: '14px',
  flexShrink: 0,
});

export const MenuItemDuplicateIcon = styled(FileCopyIcon)({
  opacity: 0.6,
  fontSize: '14px',
  flexShrink: 0,
});

// ─── Menu sizing constants ────────────────────────────────────────────────────

export const MENU_PAPER_SX = {
  minWidth: 148,
  borderRadius: '10px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
  border: '1px solid rgba(0,0,0,0.07)',
  py: '4px',
};

export const MENU_ITEM_SX = {
  px: 1.5,
  py: '5px',
  minHeight: 0,
  gap: '8px',
  '& svg': { fontSize: '14px', opacity: 0.6 },
};

export const MENU_TEXT_SX = { fontSize: 13, fontWeight: 500 };

// ─── Time entry row ───────────────────────────────────────────────────────────

export const EntryRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  height: 56,
  gap: theme.spacing(1.5),
  padding: `${theme.spacing(1.25)} ${theme.spacing(2)}`,
  borderBottom: `1px solid ${theme.palette.secondary.light}`,
  backgroundColor: theme.palette.background.paper,
  transition: 'background-color 0.15s ease',
  '&:last-of-type': { borderBottom: 'none' },
  '&:hover': { backgroundColor: 'rgba(110, 79, 247, 0.03)' },
  '& .entry-hover-icon': { visibility: 'hidden' },
  '&:hover .entry-hover-icon': { visibility: 'visible' },
}));

export const EntryDescription = styled(Typography)<{ isReadOnly?: number }>(({ isReadOnly }) => ({
  flex: 1,
  fontSize: 14,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
  cursor: isReadOnly ? 'default' : 'text',
  padding: 8,
  borderRadius: 8,
  '&:hover': { opacity: isReadOnly ? 1 : 0.7 },
}));

export const EntryDescriptionInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  fontSize: 14,
  minWidth: 0,
  backgroundColor: '#F4F6FB',
  padding: 4,
  borderRadius: 8,
  border: `1px solid ${theme.palette.primary.light}`,
}));

export const EntryPlaceholder = styled('span')({
  opacity: 0.4,
});

export const BillableIconWrapper = styled('span')({
  display: 'flex',
  alignItems: 'center',
});

export const EntryActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  flexShrink: 0,
}));

export const DatePickerWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(0.5),
  flexShrink: 0,
  width: 120,
  '& .MuiInputBase-root': {
    fontSize: 13,
    padding: 4,
    borderRadius: 8,
    border: `1px solid ${theme.palette.primary.light}`,
    '& input': { textAlign: 'center', padding: 0 },
  },
}));

export const EntryTimeRange = styled(Typography)<{ isReadOnly?: number }>(
  ({ theme, isReadOnly }) => ({
    fontVariantNumeric: 'tabular-nums',
    fontSize: 13,
    color: theme.palette.text.secondary,
    cursor: isReadOnly ? 'default' : 'text',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    '&:hover': { opacity: isReadOnly ? 1 : 0.7 },
  }),
);

export const NextDayBadge = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: 9,
  fontWeight: 700,
  color: theme.palette.text.secondary,
  lineHeight: 1,
  userSelect: 'none',
  flexShrink: 0,
  alignSelf: 'center',
  position: 'relative',
  top: -5,
}));

export const TimeEditContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

export const EntryDuration = styled(Typography)<{ iseditable?: number }>(
  ({ theme, iseditable }) => ({
    fontVariantNumeric: 'tabular-nums',
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.text.primary,
    width: 76,
    minWidth: 76,
    maxWidth: 76,
    textAlign: 'right',
    flexShrink: 0,
    cursor: iseditable ? 'text' : 'default',
    '&:hover': { opacity: iseditable ? 0.7 : 1 },
  }),
);

export const DurationInput = styled(InputBase)(({ theme }) => ({
  fontVariantNumeric: 'tabular-nums',
  fontSize: 14,
  fontWeight: 600,
  width: 76,
  minWidth: 76,
  '& input': {
    textAlign: 'right',
    padding: 0,
    color: theme.palette.text.primary,
  },
  borderBottom: `1px solid ${theme.palette.primary.light}`,
}));

export const EntryIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.disabled,
  padding: theme.spacing(0.5),
  '&:hover': { color: theme.palette.text.secondary },
}));

export const PlayIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.disabled,
  padding: theme.spacing(0.5),
  transition: 'color 0.15s',
  '&:hover': { color: theme.palette.secondary.dark },
}));

export const BillableEntryIcon = styled(AttachMoneyIcon)<{ active?: number }>(
  ({ theme, active }) => ({
    fontSize: 18,
    color: active ? theme.palette.secondary.main : theme.palette.text.disabled,
  }),
);

// ─── Summary panel ────────────────────────────────────────────────────────────

export const SummaryCard = styled(Paper)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: 'none',
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.secondary.main + '08',
}));

export const SummaryTitle = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: theme.spacing(0.5),
}));

export const SummaryTotalTime = styled(Typography)(({ theme }) => ({
  fontVariantNumeric: 'tabular-nums',
  fontSize: 28,
  fontWeight: 700,
  color: theme.palette.secondary.main,
  marginBottom: theme.spacing(1.5),
}));

export const SummarySubLabel = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.disabled,
  marginBottom: theme.spacing(0.75),
}));

export const SummaryProjectRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
}));

export const SummaryProjectDot = styled(Box)<{ dotcolor?: string }>(({ theme, dotcolor }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  flexShrink: 0,
  backgroundColor: dotcolor ?? theme.palette.text.disabled,
}));

export const SummaryProjectName = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.secondary.main,
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const SummaryProjectTime = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.text.secondary,
  fontVariantNumeric: 'tabular-nums',
  flexShrink: 0,
}));

export const SummaryTitleSuffix = styled('span')({
  fontWeight: 400,
  textTransform: 'none',
  letterSpacing: 0,
});

// ─── Entry row project badge (inline) ────────────────────────────────────────

export const EntryProjectBadge = styled(Box)<{ dotcolor?: string; isReadOnly?: number }>(({
  theme,
  dotcolor,
  isReadOnly,
}) => {
  const color = dotcolor ?? theme.palette.text.disabled;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: `6px ${theme.spacing(1)}`,
    backgroundColor: color + '1A',
    border: `1px solid ${color + '55'}`,
    borderRadius: 20,
    cursor: isReadOnly ? 'default' : 'pointer',
    userSelect: 'none',
    flexShrink: 0,
    width: 160,
    minWidth: 160,
    maxWidth: 160,
    height: 24,
    overflow: 'hidden',
    transition: 'background-color 0.15s',
    '&:hover': isReadOnly ? {} : { backgroundColor: color + '28' },
    '& .dot': {
      width: 8,
      height: 8,
      borderRadius: '50%',
      flexShrink: 0,
      backgroundColor: color,
    },
    '& .name': {
      fontSize: 11,
      fontWeight: 500,
      color: color,
      flex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      lineHeight: 1.4,
    },
    '& .chevron': {
      fontSize: 14,
      color: color + '99',
      flexShrink: 0,
    },
  };
});

// ─── Billable chip (inline in entry row) ─────────────────────────────────────

// Semantic colors for billable state — independent of workspace brand color
const BILLABLE_COLOR = '#22a06b';
const BILLABLE_BG = '#2D8C641A';
const BILLABLE_BORDER = '#22a06b55';
const NONBILLABLE_COLOR = '#8B85A8';
const NONBILLABLE_BG = '#F8F6FD';
const NONBILLABLE_BORDER = '#C4BFDA';

export const BillableChip = styled(Box)<{ active?: number; isClickable?: number }>(
  ({ active, isClickable }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 3,
    padding: `6px 6px`,
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 500,
    flexShrink: 0,
    width: 108,
    minWidth: 108,
    maxWidth: 108,
    height: 24,
    lineHeight: 1.4,
    backgroundColor: active ? BILLABLE_BG : NONBILLABLE_BG,
    color: active ? BILLABLE_COLOR : NONBILLABLE_COLOR,
    border: active ? `1px solid ${BILLABLE_BORDER}` : `1px solid ${NONBILLABLE_BORDER}`,
    cursor: isClickable ? 'pointer' : 'default',
    '& svg': { fontSize: 13, flexShrink: 0 },
  }),
);

// ─── Shared checkbox sizes ────────────────────────────────────────────────────

export const EntryRowCheckbox = styled(Checkbox)(({ theme }) => ({
  padding: 4,
  marginRight: 4,
  color: theme.palette.primary.light,
  '&.Mui-checked': {
    color: theme.palette.secondary.main,
  },
}));

export const DayGroupCheckbox = styled(Checkbox)(({ theme }) => ({
  padding: 4,
  marginRight: 8,
  color: theme.palette.primary.light,
  '&.Mui-checked': {
    color: theme.palette.secondary.main,
  },
}));

// ─── Grouped entry row ────────────────────────────────────────────────────────

export const GroupCountBadge = styled(Box)(({ theme }) => ({
  width: 20,
  height: 20,
  borderRadius: '5px',
  backgroundColor: theme.palette.brandMuted,
  color: '#fff',
  fontSize: 11,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  flexShrink: 0,
  userSelect: 'none',
  marginRight: theme.spacing(0.5),
}));

export const ChildEntriesContainer = styled(Box)(({ theme }) => ({
  borderLeft: `2px solid ${theme.palette.secondary.light}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginLeft: theme.spacing(1),
  '& > *': {
    backgroundColor: `${theme.palette.secondary.main}0A !important`,
  },
}));

// ─── Entry list messages ──────────────────────────────────────────────────────

export const EntryListMessage = styled(Box)({
  padding: 16,
  opacity: 0.5,
  fontSize: 14,
});

// ─── Project picker item helpers ──────────────────────────────────────────────

export const ProjectPickerItemContent = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flex: 1,
  minWidth: 0,
});

export const ProjectColorDot = styled(Box)<{ dotcolor?: string }>(({ dotcolor }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: dotcolor ?? '#9e9e9e',
  flexShrink: 0,
}));

export const projectPickerFormButtonSx = (open: boolean) => ({
  width: '100%',
  height: 40,
  fontSize: 13,
  fontWeight: 400,
  borderRadius: '10px',
  color: 'text.primary',
  backgroundColor: 'background.paper',
  border: '1px solid',
  borderColor: open ? 'primary.main' : 'divider',
  boxShadow: open ? (theme: any) => `0 0 0 2px ${theme.palette.primary.main}30` : 'none',
  backdropFilter: 'none',
  justifyContent: 'flex-start',
  px: 1.5,
  gap: 1,
  '&:hover': { backgroundColor: 'action.hover', borderColor: 'text.secondary' },
});

export const projectPickerFormButtonPlaceholderSx = {
  color: 'text.disabled',
};

export const projectPickerFormColorDotSx = {
  width: 9,
  height: 9,
  borderRadius: '50%',
  flexShrink: 0,
};

export const projectPickerFormNameSx = {
  flex: 1,
  textAlign: 'left' as const,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

export const projectPickerChevronStyle = {
  fontSize: 17,
  marginLeft: 'auto' as const,
  flexShrink: 0,
} as const;

export const projectPickerStarIconStyle = { fontSize: 16 } as const;
export const projectPickerFolderIconStyle = { fontSize: 17 } as const;
export const projectPickerClientCountStyle = { fontWeight: 400 } as const;
export const projectPickerNoProjectNameStyle = { color: undefined } as const;

// ─── Hero / TimerBar ──────────────────────────────────────────────────────────

export const HeroGreeting = styled(Typography)({
  fontWeight: 700,
  fontSize: 20,
  color: '#fff',
  lineHeight: 1.3,
});

export const HeroSubtitle = styled(Typography)({
  fontSize: 13,
  color: 'rgba(255,255,255,0.7)',
  marginTop: 2,
});

export const HeroModeToggleWrapper = styled(Box)({
  display: 'flex',
  gap: 4,
  backgroundColor: 'rgba(255,255,255,0.12)',
  borderRadius: 30,
  padding: '5px 6px',
});

export const HeroModeToggleButton = styled(Button, {
  shouldForwardProp: (p) => p !== 'active',
})<{ active?: number }>(({ active }) => ({
  textTransform: 'none',
  fontWeight: active ? 500 : 400,
  fontSize: 12,
  borderRadius: 22,
  padding: '4px 14px',
  minWidth: 0,
  color: '#fff',
  backgroundColor: active ? 'rgba(255,255,255,0.28)' : 'transparent',
  border: '1px solid transparent',
  boxShadow: active ? '0 2px 8px rgba(0,0,0,0.20)' : 'none',
  '&:hover': {
    backgroundColor: active ? 'rgba(255,255,255,0.32)' : 'rgba(255,255,255,0.10)',
  },
}));

export const HeroInputRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2),
}));

export const HeroDescriptionInput = styled(DescriptionInput)({
  backgroundColor: 'rgba(255,255,255,0.12)',
  borderRadius: 10,
  color: '#fff',
  flex: 1,
  border: '1px solid rgba(255,255,255,0.18)',
  transition: 'border-color 0.15s',
  '& input': { color: '#fff' },
  '& input::placeholder': { color: 'rgba(255,255,255,0.45)' },
  '&:focus-within': {
    borderColor: 'var(--hero-focus-border)',
    backgroundColor: 'rgba(255,255,255,0.16)',
  },
});

export const HeroProjectPickerWrapper = styled(Box)({
  '& .MuiButton-root': {
    color: '#fff',
    borderColor: 'rgba(255,255,255,0.3)',
    backgroundColor: 'rgba(255,255,255,0.10)',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)' },
  },
});

export const HeroBillableLabel = styled(Typography)({
  fontSize: 13,
  color: 'rgba(255,255,255,0.8)',
  whiteSpace: 'nowrap',
});

export const HeroSwitch = styled(Switch)({
  width: 34,
  height: 18,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 2,
    color: '#1b1b1b',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#000',
      '& + .MuiSwitch-track': {
        backgroundColor: '#fff',
        opacity: 1,
        border: 'none',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 14,
    height: 14,
    boxShadow: '0 1px 3px rgba(0,0,0,0.20)',
    backgroundColor: 'currentColor',
  },
  '& .MuiSwitch-track': {
    borderRadius: 11,
    backgroundColor: 'rgba(255,255,255,0.30)',
    opacity: 1,
  },
});

export const HeroActionButton = styled(Button, {
  shouldForwardProp: (p) => p !== 'stopmode',
})<{ stopmode?: number }>(({ stopmode }) => ({
  fontWeight: 600,
  fontSize: 16,
  height: 48,
  borderRadius: 10,
  textTransform: 'none',
  backgroundColor: stopmode ? '#E53935' : 'rgba(255,255,255,0.92)',
  color: stopmode ? '#fff' : '#000000',
  border: 'none',
  whiteSpace: 'nowrap',
  paddingLeft: 28,
  paddingRight: 28,
  transition: 'background-color 0.35s ease, color 0.25s ease, box-shadow 0.25s ease',
  '&:hover': {
    backgroundColor: stopmode ? '#C62828' : '#FFFFFF',
    color: stopmode ? '#fff' : '#000000',
  },
  '&:active': {
    backgroundColor: stopmode ? '#B71C1C' : '#FFFFFF',
    color: stopmode ? '#fff' : '#000000',
  },
  '&.Mui-disabled': {
    backgroundColor: 'rgba(255,255,255,0.30)',
    color: '#000000',
  },
}));

export const HeroTimerDisplay = styled(Typography)({
  fontVariantNumeric: 'tabular-nums',
  fontSize: 18,
  fontWeight: 600,
  color: '#fff',
  minWidth: 72,
  textAlign: 'center',
  letterSpacing: 1,
  cursor: 'default',
});

export const HeroManualTimeRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1.5),
}));

export const HeroTimeBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  backgroundColor: 'rgba(255,255,255,0.12)',
  borderRadius: 8,
  padding: '5px 12px',
  color: '#fff',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  border: '1px solid rgba(255,255,255,0.18)',
  '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)' },
  '& input': {
    color: '#fff',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    width: 44,
    textAlign: 'center',
    fontSize: 13,
  },
});

export const HeroDurationBadge = styled(Typography)({
  fontSize: 13,
  fontWeight: 600,
  color: 'rgba(255,255,255,0.9)',
  backgroundColor: 'rgba(255,255,255,0.12)',
  borderRadius: 8,
  padding: '5px 12px',
  minWidth: 72,
  textAlign: 'center',
});

// ─── TimerBar layout ──────────────────────────────────────────────────────────

export const HeroTopRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
});

export const HeroLiveActions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const HeroDiscardButton = styled(IconButton)({
  color: 'rgba(255,255,255,0.75)',
  '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' },
});

export const HeroStartTimePopoverContent = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  paddingLeft: 20,
  paddingRight: 20,
  paddingTop: 20,
  paddingBottom: 20,
});

// ─── Discard confirmation popover ─────────────────────────────────────────────

export const DiscardPopoverContent = styled(Box)({
  padding: 16,
  borderRadius: 12,
  minWidth: 220,
});

export const DiscardPopoverTitle = styled(Typography)({
  fontSize: 13,
  fontWeight: 600,
  marginBottom: 4,
});

export const DiscardPopoverBody = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
  marginBottom: 12,
}));

export const DiscardPopoverActions = styled(Box)({
  display: 'flex',
  gap: 8,
  justifyContent: 'flex-end',
});

export const DiscardCancelButton = styled(Button)({
  fontSize: 12,
  textTransform: 'none',
});

export const DiscardConfirmButton = styled(Button)({
  fontSize: 12,
  textTransform: 'none',
  borderRadius: '8px',
});

export const DISCARD_POPOVER_PAPER_SX = {
  mt: 1,
  p: 2,
  borderRadius: '12px',
  minWidth: 220,
  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
};
